import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { combineLatest, filter, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import {
    SharedAdvancedRuleDetailDialogComponent
} from 'src/app/_shared/dialogs/shared-advanced-rules/shared-advanced-rule-detail-dialog/shared-advanced-rule-detail-dialog.component';
import { AdvancedRule } from 'src/app/_shared/services/advanced-rule/advanced-rule.service';
import { AllocationsContext, AllocationService } from 'src/app/_shared/services/allocations/detail/allocation.service';
import {
    SharedAllocationExecutionCode
} from 'src/app/_shared/stores/shared-allocation-execution/shared-allocation-execution.actions';
import { AttributionBudgetModelDto } from 'src/graphql/generated-types';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-shared-allocation-detail',
    templateUrl: './shared-allocation-detail.component.html',
    styleUrls: ['./shared-allocation-detail.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedAllocationDetailComponent extends BaseComponent implements OnInit {

    selectedTabControl = new FormControl<number | null>(null);

    isEnvelopeArchived = false;

    currentDelegationId: number | null = null;

    allocation: AttributionBudgetModelDto | null = null;

    advancedAccessibilityRule: AdvancedRule | null = null;
    advancedSubventionRule: AdvancedRule | null = null;

    loading$ = this.allocationService.loading$;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly layoutService: LayoutService,
                private readonly dialogService: KalDialogService,
                public readonly allocationService: AllocationService) {
        super(cdr);
    }

    get type(): SharedAllocationExecutionCode {
        return this.allocationService.type;
    }

    get canExecute(): boolean {
        return !this.allocation?.IsCloture && !this.isEnvelopeArchived;
    }

    get canCancelExecution(): boolean {
        return !!this.allocation?.AttribTgExec && this.allocation?.IdDelegation === this.currentDelegationId;
    }

    get canSettle(): boolean {
        return this.isVirtualWallet;
    }

    get isVirtualWallet(): boolean {
        return this.allocationService.type === AllocationsContext.PMV;
    }

    get canEdit(): boolean {
        const isChequesAllocation = this.allocationService.type === AllocationsContext.ART;
        const isCreationDelegation = this.allocation?.IdDelegation === this.currentDelegationId;

        return !this.allocation?.IsCloture && isCreationDelegation
            && (!this.allocation?.AttribTgExec || !isChequesAllocation);
    }

    get canDelete(): boolean {
        return !this.allocation?.IsCloture && !this.allocation?.AttribTgExec;
    }

    get canArchive(): boolean {
        return !this.allocation?.IsCloture && !!this.allocation?.AttribTgExec;
    }

    navigateBackToList(): void {
        this.allocationService.navigateToList();
    }

    execute(): void {
        this.watch$ = this.allocationService.execute(this.allocation);
    }

    cancelExecution(): void {
        this.watch$ = this.allocationService.cancelExecution(this.allocation);
    }

    settle(): void {
        this.watch$ = this.allocationService.settle$(this.allocation);
    }

    edit(): void {
        this.watch$ = this.allocationService.edit(this.allocation);
    }

    delete(): void {
        this.watch$ = this.allocationService.delete(this.allocation);
    }

    duplicate(): void {
        this.watch$ = this.allocationService.duplicate(this.allocation);
    }

    archive(): void {
        const config = new KalDialogConfig<ConfirmDialogData>({
            title: 'Archiver l\'attribution',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                body: `Souhaitez-vous archiver l'attribution "${this.allocation.Libelle}" ?`,
                submitLabel: 'Archiver',
                cancelLabel: 'Annuler'
            }
        });

        this.watch$ = this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
            filter(data => !!data?.accepted),
            switchMap(() => this.allocationService.archive(this.allocation))
        );
    }

    navigateToList(): void {
        this.watch$ = this.allocationService.navigateToList();
    }

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    openAdvancedRuleDetailDialog(rule: AdvancedRule): void {
        this.dialogService.open(SharedAdvancedRuleDetailDialogComponent, SharedAdvancedRuleDetailDialogComponent.config({
            rule,
            ruleLabel: rule.Libelle,
            ruleType: rule.TypeRegle,
            id: rule.Id,
            context: this.type
        }));
    }

    ngOnInit(): void {
        this.watch$ = this.allocationService
            .initialize$()
            .pipe(
                tap(() => {
                    this.selectedTabControl.patchValue(0, {emitEvent: false});
                    this.cdr.markForCheck();
                })
            );

        this.watch$ = combineLatest([
            this.allocationService.allocation$,
            this.allocationService.currentDelegation$,
            this.allocationService.isAllocationEnvelopeArchived$,
            this.allocationService.allocationAdvancedAccessibilityRule$,
            this.allocationService.allocationAdvancedSubventionRule$
        ]).pipe(
            map(([allocation, delegation, isEnvelopeArchived, advancedAccessibilityRule, advancedSubventionRule]) => {
                this.allocation = allocation;
                this.currentDelegationId = delegation?.Id;
                this.isEnvelopeArchived = isEnvelopeArchived;
                this.advancedAccessibilityRule = advancedAccessibilityRule;
                this.advancedSubventionRule = advancedSubventionRule;
                this.cdr.markForCheck();
            })
        );
    }
}
